import { watchEffect } from "vue";
import { PaymentMethod } from "@smeetz/order-common";
import type { IOfferV1 } from "@smeetz/offers-common";
import { useOrderStore } from "@smeetz/order-store";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { useGroupOffersStore } from "@/stores/group-offers.store";
import { LocalStorageKeys } from "@/services/storage.service";
import { initializeOrderSessionDataService } from "@/services/order-session-data.service";
import { BookingStep } from "@/constants/booking";
import {
  createNewOrder,
  selectLastSellableOrder,
  selectOrder,
  setupOrderSessionExpiryHandling,
} from "../vue-helpers/orders.helper";
import { setupOrderStockIndexing } from "../vue-helpers/order-stock.helper";

let resolvePiniaStoreReady: (() => void) | null = null;

/**
 * Resolves once `initPiniaStore` has finished (session data available, orders
 * restored/created, persistence watchers installed). Await it before creating
 * orders outside the bootstrap (e.g. on cashfloat open) to avoid racing the
 * bootstrap's own first-order creation.
 */
export const piniaStoreReady = new Promise<void>((resolve) => {
  resolvePiniaStoreReady = resolve;
});

export async function initPiniaStore(): Promise<void> {
  try {
    // Wait for the session data to be initialized before proceeding
    await initializeOrderSessionDataService();

    setupOrderSessionExpiryHandling();
    setupOrderStockIndexing();

    // Meta first: restoring orders can trigger expiry handling for the
    // restored selection, which cleans up per-order meta — that cleanup must
    // run against the restored meta, not be overwritten by it afterwards.
    restoreOrdersMeta();
    restoreGroupOffers();
    await restoreOrders();

    watchEffect(persistOrders);
    watchEffect(persistOrdersMeta);
    watchEffect(persistGroupOffers);
  } finally {
    resolvePiniaStoreReady?.();
  }
}

export function persistOrders(): void {
  const orderStore = useOrderStore();
  localStorage.setItem(
    LocalStorageKeys.Orders,
    JSON.stringify({
      orders: orderStore.orders,
      selectedOrder: orderStore.selectedOrder,
    })
  );
}

export async function restoreOrders(): Promise<void> {
  const orderState = JSON.parse(
    localStorage.getItem(LocalStorageKeys.Orders) || "{}"
  );

  const orderStore = useOrderStore();
  if (orderStore.orderCount > 0) {
    // Orders already exist (e.g. created by the cashfloat-open flow while the
    // bootstrap was still pending) — don't clobber them or create a duplicate.
    return;
  }
  if (Object.keys(orderState.orders || {}).length === 0) {
    try {
      await createNewOrder();
    } catch {
      // Journaled + surfaced by the order helper. The POS starts without a
      // pre-created order; the cashier can open one from the navbar.
    }
    return;
  }

  orderStore.setOrders(orderState.orders);
  const savedReference = orderState.selectedOrder?.reference;
  if (savedReference) {
    selectOrder(savedReference);
  }
  if (!orderStore.selectedOrder && !selectLastSellableOrder()) {
    // The saved selection no longer resolves (its order is gone from the
    // restored set, or the persisted state lacks one) and no restored tab is
    // still sellable. Without a selected order the store has no active Order
    // instance and every order action fails with "No selected order" — start
    // the till on a fresh order instead.
    try {
      await createNewOrder();
    } catch {
      // Journaled + surfaced by the order helper. The POS starts without a
      // pre-created order; the cashier can open one from the navbar.
    }
  }
}

export function persistOrdersMeta(): void {
  const orderMetaStore = useOrderMetaStore();
  localStorage.setItem(
    LocalStorageKeys.OrdersMeta,
    JSON.stringify({
      ordersMeta: orderMetaStore.ordersMeta,
      // Tab naming survives reloads: restored orders keep their (stamped)
      // names, so the counter must continue where it left off or new tabs
      // would duplicate existing "Order #N" names.
      tabNameCounter: orderMetaStore.tabNameCounter,
      recentTabNames: orderMetaStore.recentTabNames,
    })
  );
}

export function restoreOrdersMeta(): void {
  const orderMetaState = JSON.parse(
    localStorage.getItem(LocalStorageKeys.OrdersMeta) || "{}"
  );

  if (!orderMetaState || !orderMetaState.ordersMeta) {
    orderMetaState.ordersMeta = {
      ordersMeta: {},
      step: BookingStep.Order,
      paymentMethod: PaymentMethod.CASH,
      currency: null,
      providedAmount: 0,
    };
  }

  const orderMetaStore = useOrderMetaStore();
  orderMetaStore.ordersMeta = orderMetaState.ordersMeta;
  if (typeof orderMetaState.tabNameCounter === "number") {
    orderMetaStore.tabNameCounter = orderMetaState.tabNameCounter;
  }
  if (Array.isArray(orderMetaState.recentTabNames)) {
    orderMetaStore.recentTabNames = orderMetaState.recentTabNames;
  }
}

export function persistGroupOffers(): void {
  const groupOffersStore = useGroupOffersStore();
  localStorage.setItem(
    LocalStorageKeys.GroupOffers,
    JSON.stringify({ offers: groupOffersStore.offers })
  );
}

export function restoreGroupOffers(): void {
  const stored = JSON.parse(
    localStorage.getItem(LocalStorageKeys.GroupOffers) || "{}"
  );
  const offers: IOfferV1[] = Array.isArray(stored?.offers) ? stored.offers : [];
  if (offers.length === 0) return;

  const groupOffersStore = useGroupOffersStore();
  groupOffersStore.setOffers(offers);
}
